import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight, Minus, Plus, Ticket, CalendarRange, Save, FolderOpen, Loader2 } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useFarmStore } from '@/store/useFarmStore';
import { toast } from 'sonner';
import { OrderFrequency, SavedCart } from '@shared/types';
import { useCurrency } from '@/hooks/use-currency';
import { api } from '@/lib/api-client';

export function CartPage() {
  const cart = useFarmStore((s) => s.cart);
  const updateQuantity = useFarmStore((s) => s.updateQuantity);
  const removeFromCart = useFarmStore((s) => s.removeFromCart);
  const setCart = useFarmStore((s) => s.setCart);
  const activePromo = useFarmStore((s) => s.activePromo);
  const applyPromo = useFarmStore((s) => s.applyPromo);
  const [promoInput, setPromoInput] = React.useState('');
  const [savedCarts, setSavedCarts] = useState<SavedCart[]>([]);
  const [savedCartsLoading, setSavedCartsLoading] = useState(false);
  const [saveCartDialogOpen, setSaveCartDialogOpen] = useState(false);
  const [cartName, setCartName] = useState('');
  const navigate = useNavigate();
  const { formatFromBase } = useCurrency();

  useEffect(() => {
    setSavedCartsLoading(true);
    api.get('/api/profile/saved-carts')
      .then(res => res.json())
      .then(data => setSavedCarts(data.carts || []))
      .catch(() => setSavedCarts([]))
      .finally(() => setSavedCartsLoading(false));
  }, []);

  const saveCart = async () => {
    if (!cartName.trim()) {
      toast.error('Please enter a name for this cart');
      return;
    }
    try {
      const res = await api.post('/api/profile/saved-carts', {
        body: JSON.stringify({
          name: cartName,
          items: cart.map(item => ({
            productId: item.product.id,
            quantity: item.quantity,
            frequency: item.frequency
          }))
        })
      });
      const data = await res.json();
      setSavedCarts(prev => [data.cart, ...prev]);
      setSaveCartDialogOpen(false);
      setCartName('');
      toast.success('Cart saved!');
    } catch {
      toast.error('Failed to save cart');
    }
  };

  const loadCart = (savedCart: SavedCart) => {
    const items = savedCart.items.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      frequency: item.frequency as OrderFrequency
    }));
    setCart(items);
    toast.success(`Loaded "${savedCart.name}"`);
  };

  const deleteSavedCart = async (id: string) => {
    try {
      await api(`/api/profile/saved-carts/${id}`, { method: 'DELETE' });
      setSavedCarts(prev => prev.filter(c => c.id !== id));
      toast.success('Cart deleted');
    } catch {
      toast.error('Failed to delete cart');
    }
  };
  const subtotal = cart.reduce((acc, item) => {
    // Apply 10% discount for monthly subscriptions
    const itemTotal = item.frequency === 'monthly' ? item.subtotal * 0.9 : item.subtotal;
    return acc + itemTotal;
  }, 0);
  const discount = activePromo
    ? (activePromo.discountType === 'percentage'
        ? (subtotal * activePromo.value) / 100
        : activePromo.value)
    : 0;
  const total = Math.max(0, subtotal - discount);
  const handlePromo = () => {
    if (applyPromo(promoInput)) {
      toast.success('Promo code applied!');
      setPromoInput('');
    } else {
      toast.error('Invalid promo code');
    }
  };
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
          <div className="p-6 bg-muted rounded-full">
            <ShoppingBag className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold">Your cart is empty</h2>
          <p className="text-muted-foreground">Looks like you haven't added any packages or items yet.</p>
          <Button asChild className="px-8 rounded-md">
            <Link to="/wizard">Start Farming Wizard</Link>
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-muted/20">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <h1 className="text-3xl font-bold mb-8">Cart & Subscriptions</h1>
        
        {savedCarts.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2"><FolderOpen className="h-5 w-5" /> Saved Carts</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedCarts.map(sc => (
                <Card key={sc.id} className="border shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => loadCart(sc)}>
                      <p className="font-medium text-sm">{sc.name}</p>
                      <p className="text-xs text-muted-foreground">{sc.items.length} items • {new Date(sc.updatedAt).toLocaleDateString()}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); deleteSavedCart(sc.id); }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={`${item.product.id}-${item.frequency}`} className="overflow-hidden border shadow-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">{item.product.name}</h3>
                        {item.frequency === 'monthly' && (
                          <span className="text-[10px] bg-primary/10 text-primary font-bold px-1.5 py-0.5 rounded uppercase">Save 10%</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground capitalize">{item.product.category} • {item.product.subcategory}</p>
                    </div>
                    <div className="flex flex-col sm:items-end gap-3">
                      {item.product.isSubscriptionAvailable && (
                        <div className="flex items-center gap-2">
                          <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                          <Select
                            value={item.frequency}
                            onValueChange={(val: OrderFrequency) => updateQuantity(item.product.id, item.quantity, val)}
                          >
                            <SelectTrigger className="h-8 text-xs w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="one-time">One-time</SelectItem>
                              <SelectItem value="monthly">Monthly Plan</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center border rounded-md bg-background h-8">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product.id, Math.max(1, item.quantity - 1))}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-xs font-medium">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className="font-bold text-sm">
                            {formatFromBase(item.frequency === 'monthly' ? item.subtotal * 0.9 : item.subtotal)}
                          </div>
                          <div className="text-[10px] text-muted-foreground">{formatFromBase(item.product.price)}/{item.product.unit}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          onClick={() => removeFromCart(item.product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="space-y-6">
            <Card className="border shadow-sm">
              <CardContent className="p-6 space-y-6">
                <h2 className="text-lg font-bold">Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Standard Subtotal</span>
                    <span>{formatFromBase(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-xs text-green-600 font-bold">
                      <span>Promo ({activePromo?.code})</span>
                      <span>-{formatFromBase(discount)}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t flex justify-between font-bold text-base">
                    <span>Total Cost</span>
                    <span className="text-primary">{formatFromBase(total)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground">Promotion</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Ticket className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        placeholder="CODE"
                        className="pl-8 h-9 text-xs"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                      />
                    </div>
                    <Button variant="outline" size="sm" className="h-9" onClick={handlePromo}>Apply</Button>
                  </div>
                </div>
                <Button className="w-full h-11 rounded-md" onClick={() => navigate('/checkout')}>
                  Checkout <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <div className="flex gap-2">
                  <Dialog open={saveCartDialogOpen} onOpenChange={setSaveCartDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex-1 h-9 rounded-md">
                        <Save className="h-4 w-4 mr-2" /> Save Cart
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Save Cart</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Cart Name</Label>
                          <Input value={cartName} onChange={(e) => setCartName(e.target.value)} placeholder="e.g., My Farm Setup" />
                        </div>
                        <p className="text-xs text-muted-foreground">Save your current cart items to load later.</p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveCartDialogOpen(false)}>Cancel</Button>
                        <Button onClick={saveCart}><Save className="h-4 w-4 mr-2" /> Save</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-[10px] text-muted-foreground text-center">
                  Prices exclude regional taxes. Monthly plans billed recurringly starting M2.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}